/**
 * Turning what Xero and the Sales Hub said into forecast lines, the same way
 * on the server and in the browser:
 *
 * - Xero's unpaid bills are money out and its unpaid sales invoices money in,
 *   on their due dates (or tomorrow, if already due). A line entered here that
 *   is linked to a Xero contact takes that contact's documents as its own, and
 *   its schedule only counts after the latest of them.
 * - The run-rate is Xero's receipts over recent complete months, less the
 *   contacts left out, averaged: expected money in, weekly.
 * - The Sales Hub's billing schedule is matched to Xero's sales invoices.
 *   Raised ones are Xero's to count; ones still to come are expected money in
 *   after the payment terms; ones already due but never raised are the billing
 *   gap, and stay out of the forecast until someone acts on them.
 */
import { addDays, addMonths, daysBetween, mondayOf, monthKey, type ISODate } from "./dates";
import type { Basis, Direction, Schedule } from "./engine";
import type { FinanceModel, FinanceSettings, HubBillingLine, XeroDoc, XeroReceipt } from "./types";

const later = (a: ISODate, b: ISODate) => (a > b ? a : b);

/* ------------------------------------------------------------------ */
/* Run-rate                                                            */
/* ------------------------------------------------------------------ */

export interface RunRate {
  /** Pence a month, with VAT: what customers paid on average. */
  monthly: number;
  months: { month: string; total: number }[];
  contacts: { contactId: string; contactName: string; total: number; excluded: boolean }[];
}

/** Receipts over the last complete months before `today`, less the contacts left out, averaged. Null without receipts. */
export function runRate(receipts: XeroReceipt[], settings: Pick<FinanceSettings, "runRateMonths" | "runRateExcluded">, today: ISODate): RunRate | null {
  if (!receipts.length) return null;
  const firstOfMonth = `${monthKey(today)}-01`;
  const months = Array.from({ length: settings.runRateMonths }, (_, i) => monthKey(addMonths(firstOfMonth, i - settings.runRateMonths, 1)));
  const inWindow = new Set(months);
  const excluded = new Set(settings.runRateExcluded.map((c) => c.toLowerCase()));
  const byMonth = new Map(months.map((m) => [m, 0]));
  const byContact = new Map<string, { contactId: string; contactName: string; total: number; excluded: boolean }>();
  for (const r of receipts) {
    const m = monthKey(r.date);
    if (!inWindow.has(m)) continue;
    const id = r.contactId.toLowerCase();
    const c = byContact.get(id) ?? { contactId: id, contactName: r.contactName, total: 0, excluded: excluded.has(id) };
    c.total += r.amount;
    byContact.set(id, c);
    if (!c.excluded) byMonth.set(m, byMonth.get(m)! + r.amount);
  }
  const counted = [...byMonth.values()].reduce((a, b) => a + b, 0);
  return {
    monthly: Math.round(counted / settings.runRateMonths),
    months: months.map((month) => ({ month, total: byMonth.get(month)! })),
    contacts: [...byContact.values()].sort((a, b) => b.total - a.total),
  };
}

/* ------------------------------------------------------------------ */
/* The Sales Hub's billing schedule against Xero                       */
/* ------------------------------------------------------------------ */

export type BillingState = "raised" | "expected" | "unbilled" | "no_contact";

export interface BillingMatch {
  line: HubBillingLine;
  state: BillingState;
  /** The Xero invoice that raised it. */
  invoice: XeroDoc | null;
  /** Pence, with VAT. */
  gross: number;
}

/**
 * Each scheduled invoice, matched to the nearest unused Xero sales invoice for
 * the customer's contacts dated within half a period of it (45 days for
 * quarterly, 75 for annual).
 */
export function matchBilling(lines: HubBillingLine[], docs: XeroDoc[], today: ISODate, vatPct: number): BillingMatch[] {
  const invoices = docs.filter((d) => d.kind === "invoice");
  const used = new Set<string>();
  return [...lines]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((line) => {
      const contacts = new Set(line.xeroContactIds.map((c) => c.toLowerCase()));
      const window = line.schedule === "quarterly" ? 45 : 75;
      let best: XeroDoc | null = null;
      for (const inv of invoices) {
        if (used.has(inv.id) || !contacts.has(inv.contactId.toLowerCase())) continue;
        const gap = Math.abs(daysBetween(line.date, inv.date));
        if (gap > window) continue;
        if (!best || gap < Math.abs(daysBetween(line.date, best.date))) best = inv;
      }
      if (best) used.add(best.id);
      const state: BillingState = best ? "raised" : line.date > today ? "expected" : contacts.size ? "unbilled" : "no_contact";
      return { line, state, invoice: best, gross: Math.round(line.net * (1 + vatPct / 100)) };
    });
}

/* ------------------------------------------------------------------ */
/* Forecast lines from the sources                                     */
/* ------------------------------------------------------------------ */

export interface SourceRow extends Schedule {
  /** What the amount is: an invoice number, a customer. */
  label: string;
}

export interface SourceLine {
  id: string;
  name: string;
  direction: Direction;
  basis: Basis;
  origin: "xero" | "hub";
  /** The line entered here that this belongs to (linked to the Xero contact), if any. */
  linkedTo: string | null;
  rows: SourceRow[];
}

/** For each entered line linked to a Xero contact: the latest due date among that contact's documents, which the schedule only counts after. */
export function coveredUntil(model: Pick<FinanceModel, "commitments" | "synced">): Map<string, ISODate> {
  const out = new Map<string, ISODate>();
  for (const c of model.commitments) {
    if (!c.active || !c.xeroContactId) continue;
    const kind = c.direction === "out" ? "bill" : "invoice";
    let last: ISODate | null = null;
    for (const d of model.synced.xeroDocs) if (d.kind === kind && d.contactId.toLowerCase() === c.xeroContactId && (!last || d.dueDate > last)) last = d.dueDate;
    if (last) out.set(c.id, last);
  }
  return out;
}

export function sourceLines(model: Pick<FinanceModel, "commitments" | "synced" | "settings">, asOf: ISODate): SourceLine[] {
  const tomorrow = addDays(asOf, 1);
  const lines: SourceLine[] = [];
  const linked = new Map<string, string>();
  // a contact covering more than one line shows its documents on the first
  for (const c of model.commitments) if (c.active && c.xeroContactId && !linked.has(`${c.direction}:${c.xeroContactId}`)) linked.set(`${c.direction}:${c.xeroContactId}`, c.id);

  // Xero: what's still owed, each way, by contact
  const owed = new Map<string, XeroDoc[]>();
  for (const d of model.synced.xeroDocs) {
    if (d.amountDue <= 0) continue;
    const key = `${d.kind}:${d.contactId.toLowerCase()}`;
    owed.set(key, [...(owed.get(key) ?? []), d]);
  }
  for (const [key, docs] of owed) {
    const kind = docs[0]!.kind;
    const contact = docs[0]!.contactId.toLowerCase();
    const direction: Direction = kind === "bill" ? "out" : "in";
    lines.push({
      id: `xero:${key}`,
      name: docs[0]!.contactName,
      direction,
      basis: "contracted",
      origin: "xero",
      linkedTo: linked.get(`${direction}:${contact}`) ?? null,
      rows: docs.map((d) => ({ date: later(d.dueDate, tomorrow), repeat: null, until: null, amount: d.amountDue, label: `${d.number || (kind === "bill" ? "Bill" : "Invoice")}, due ${d.dueDate}` })),
    });
  }

  // Xero: receipts at their recent run-rate
  const rate = runRate(model.synced.receipts, model.settings, asOf);
  if (rate && rate.monthly > 0)
    lines.push({
      id: "xero:runrate",
      name: "Customer receipts at run-rate",
      direction: "in",
      basis: "expected",
      origin: "xero",
      linkedTo: null,
      rows: [{ date: addDays(mondayOf(asOf), 7), repeat: "week", until: null, amount: Math.round((rate.monthly * 12) / 52), label: `${model.settings.runRateMonths}-month average of Xero receipts` }],
    });

  // Sales Hub: invoices still to come, paid after the payment terms
  const matches = matchBilling(model.synced.hubBilling, model.synced.xeroDocs, asOf, model.settings.salesVatPct).filter((m) => m.state === "expected");
  if (matches.length)
    lines.push({
      id: "hub:billing",
      name: "Customer billing",
      direction: "in",
      basis: "expected",
      origin: "hub",
      linkedTo: null,
      rows: matches.map((m) => ({
        date: later(addDays(m.line.date, model.settings.paymentTermsDays), tomorrow),
        repeat: null,
        until: null,
        amount: m.gross,
        label: `${m.line.organisationName}${m.line.afterFreePeriod ? " (after its free period)" : ""}`,
      })),
    });
  return lines;
}
